// Roles y permisos. La misma lógica se replica en SQL (app.has_permission) para que
// el servidor rechace lo que la interfaz oculta.

export type Role = 'owner' | 'employee';

/** Permisos que el dueño puede habilitar a cada empleado. */
export const EMPLOYEE_PERMISSIONS = [
  { key: 'edit_prices', label: 'Editar precios', description: 'Cambiar precios de venta de los productos.' },
  { key: 'apply_discounts', label: 'Aplicar descuentos', description: 'Hacer descuentos en una venta, hasta el tope indicado.' },
  { key: 'void_sales', label: 'Anular ventas', description: 'Anular ventas y registrar devoluciones.' },
  { key: 'adjust_stock', label: 'Ajustar stock', description: 'Registrar ajustes y mermas de stock.' },
  { key: 'receive_purchases', label: 'Cargar compras', description: 'Registrar compras a proveedores.' },
  { key: 'manage_customers', label: 'Gestionar clientes', description: 'Crear y editar clientes.' },
  { key: 'customer_accounts', label: 'Cuenta corriente', description: 'Vender a cuenta corriente y registrar pagos de clientes.' },
] as const;

export type EmployeePermission = (typeof EMPLOYEE_PERMISSIONS)[number]['key'];

export type PermissionSet = Partial<Record<EmployeePermission, boolean>> & {
  /** Tope de descuento por venta, en %. Solo aplica si apply_discounts está habilitado. */
  max_discount_pct?: number;
};

/** Lo que cualquier empleado activo puede hacer siempre. */
const EMPLOYEE_BASE = ['sell', 'view_stock', 'operate_own_cash'] as const;

/** Exclusivo del dueño: nunca se puede delegar a un empleado. */
const OWNER_ONLY = [
  'view_costs',
  'view_reports',
  'export_data',
  'manage_products',
  'manage_settings',
  'manage_employees',
  'manage_devices',
] as const;

export type Capability =
  | EmployeePermission
  | (typeof EMPLOYEE_BASE)[number]
  | (typeof OWNER_ONLY)[number];

export interface Actor {
  role: Role;
  permissions: PermissionSet;
  active: boolean;
}

const PERMISSION_KEYS = new Set<string>(EMPLOYEE_PERMISSIONS.map((p) => p.key));
const DEFAULT_MAX_DISCOUNT_PCT = 10;

export function can(actor: Actor | null | undefined, capability: Capability): boolean {
  if (!actor || !actor.active) return false;
  if (actor.role === 'owner') return true;
  if ((EMPLOYEE_BASE as readonly string[]).includes(capability)) return true;
  if (PERMISSION_KEYS.has(capability)) {
    return actor.permissions[capability as EmployeePermission] === true;
  }
  return false;
}

export function maxDiscountPct(actor: Actor | null | undefined): number {
  if (!actor || !actor.active) return 0;
  if (actor.role === 'owner') return 100;
  if (!actor.permissions.apply_discounts) return 0;
  return clampPct(actor.permissions.max_discount_pct ?? DEFAULT_MAX_DISCOUNT_PCT);
}

/** Limpia un objeto de permisos que viene de la base (jsonb) o de un formulario. */
export function normalizePermissions(raw: unknown): PermissionSet {
  const result: PermissionSet = {};
  if (!raw || typeof raw !== 'object') return result;
  const obj = raw as Record<string, unknown>;
  for (const key of PERMISSION_KEYS) {
    if (obj[key] === true) result[key as EmployeePermission] = true;
  }
  if (typeof obj.max_discount_pct === 'number' && Number.isFinite(obj.max_discount_pct)) {
    result.max_discount_pct = clampPct(obj.max_discount_pct);
  }
  return result;
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}
